import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Plus, Pencil, Trash2, Database, X, FileCheck } from 'lucide-react';

interface SatProdServ {
  id: number;
  clave: string;
  descripcion: string;
  isActive: boolean;
}

interface SatUnidad {
  id: number;
  clave: string;
  nombre: string;
  descripcion: string | null;
  isActive: boolean;
}

type Tab = 'prod-serv' | 'unidades';

export default function SatCatalogos() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('prod-serv');
  const [seedLoading, setSeedLoading] = useState(false);

  // Prod/serv form
  const [showProdServForm, setShowProdServForm] = useState(false);
  const [editingProdServ, setEditingProdServ] = useState<SatProdServ | null>(null);
  const [prodServForm, setProdServForm] = useState({ clave: '', descripcion: '' });

  // Unidades form
  const [showUnidadForm, setShowUnidadForm] = useState(false);
  const [editingUnidad, setEditingUnidad] = useState<SatUnidad | null>(null);
  const [unidadForm, setUnidadForm] = useState({ clave: '', nombre: '', descripcion: '' });

  const { data: prodServList = [], isLoading: loadingPS } = useQuery<SatProdServ[]>({
    queryKey: ['sat-prod-serv'],
    queryFn: async () => {
      const r = await fetch('/api/cfdi/sat/prod-serv', { credentials: 'include' });
      if (!r.ok) throw new Error('Error');
      return r.json();
    },
  });

  const { data: unidadesList = [], isLoading: loadingU } = useQuery<SatUnidad[]>({
    queryKey: ['sat-unidades'],
    queryFn: async () => {
      const r = await fetch('/api/cfdi/sat/unidades', { credentials: 'include' });
      if (!r.ok) throw new Error('Error');
      return r.json();
    },
  });

  const saveProdServMutation = useMutation({
    mutationFn: async (data: { clave: string; descripcion: string; id?: number }) => {
      const url = data.id ? `/api/cfdi/sat/prod-serv/${data.id}` : '/api/cfdi/sat/prod-serv';
      const r = await fetch(url, {
        method: data.id ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clave: data.clave, descripcion: data.descripcion }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Error'); }
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sat-prod-serv'] });
      toast({ title: 'Guardado' });
      setShowProdServForm(false);
      setEditingProdServ(null);
      setProdServForm({ clave: '', descripcion: '' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const deleteProdServMutation = useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/cfdi/sat/prod-serv/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Error'); }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sat-prod-serv'] });
      toast({ title: 'Eliminado' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const saveUnidadMutation = useMutation({
    mutationFn: async (data: { clave: string; nombre: string; descripcion?: string; id?: number }) => {
      const url = data.id ? `/api/cfdi/sat/unidades/${data.id}` : '/api/cfdi/sat/unidades';
      const r = await fetch(url, {
        method: data.id ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clave: data.clave, nombre: data.nombre, descripcion: data.descripcion }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Error'); }
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sat-unidades'] });
      toast({ title: 'Guardado' });
      setShowUnidadForm(false);
      setEditingUnidad(null);
      setUnidadForm({ clave: '', nombre: '', descripcion: '' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const deleteUnidadMutation = useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/cfdi/sat/unidades/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Error'); }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sat-unidades'] });
      toast({ title: 'Eliminado' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const handleSeed = async () => {
    setSeedLoading(true);
    try {
      const r = await fetch('/api/cfdi/sat/seed', { method: 'POST', credentials: 'include' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Error'); }
      const data = await r.json();
      queryClient.invalidateQueries({ queryKey: ['sat-prod-serv'] });
      queryClient.invalidateQueries({ queryKey: ['sat-unidades'] });
      toast({ title: 'Catálogos cargados', description: `${data.unidades} unidades y ${data.prodServ} claves de prod/serv agregadas.` });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSeedLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Catálogos SAT</h1>
            <p className="text-muted-foreground">Claves del SAT para emisión de CFDI 4.0</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleSeed} disabled={seedLoading}>
          {seedLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileCheck className="mr-2 h-4 w-4" />}
          Cargar catálogos base
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab('prod-serv')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'prod-serv' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Productos y Servicios ({prodServList.length})
        </button>
        <button
          onClick={() => setTab('unidades')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'unidades' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Unidades de Medida ({unidadesList.length})
        </button>
      </div>

      {/* Prod/Serv tab */}
      {tab === 'prod-serv' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Claves de Producto/Servicio SAT</CardTitle>
              <Button size="sm" onClick={() => { setEditingProdServ(null); setProdServForm({ clave: '', descripcion: '' }); setShowProdServForm(true); }}>
                <Plus className="h-4 w-4 mr-1" />
                Agregar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingPS ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : prodServList.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Sin claves. Usa "Cargar catálogos base" para añadir las claves educativas estándar.</p>
            ) : (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {prodServList.map((ps) => (
                  <div key={ps.id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50 group">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded shrink-0">{ps.clave}</span>
                      <span className="text-sm truncate">{ps.descripcion}</span>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 shrink-0 ml-2">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                        setEditingProdServ(ps);
                        setProdServForm({ clave: ps.clave, descripcion: ps.descripcion });
                        setShowProdServForm(true);
                      }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => {
                        if (!confirm(`¿Eliminar clave ${ps.clave}?`)) return;
                        deleteProdServMutation.mutate(ps.id);
                      }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Unidades tab */}
      {tab === 'unidades' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Unidades de Medida SAT</CardTitle>
              <Button size="sm" onClick={() => { setEditingUnidad(null); setUnidadForm({ clave: '', nombre: '', descripcion: '' }); setShowUnidadForm(true); }}>
                <Plus className="h-4 w-4 mr-1" />
                Agregar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingU ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : unidadesList.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Sin unidades. Usa "Cargar catálogos base" para añadir las unidades estándar.</p>
            ) : (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {unidadesList.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50 group">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded shrink-0 w-14 text-center">{u.clave}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{u.nombre}</p>
                        {u.descripcion && <p className="text-xs text-muted-foreground truncate">{u.descripcion}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 shrink-0 ml-2">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                        setEditingUnidad(u);
                        setUnidadForm({ clave: u.clave, nombre: u.nombre, descripcion: u.descripcion || '' });
                        setShowUnidadForm(true);
                      }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => {
                        if (!confirm(`¿Eliminar unidad ${u.clave}?`)) return;
                        deleteUnidadMutation.mutate(u.id);
                      }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Prod/Serv form modal */}
      {showProdServForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">{editingProdServ ? 'Editar Clave SAT' : 'Nueva Clave SAT'}</h2>
              <button onClick={() => { setShowProdServForm(false); setEditingProdServ(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Clave SAT (8 dígitos) *</Label>
                <Input
                  value={prodServForm.clave}
                  onChange={(e) => setProdServForm({ ...prodServForm, clave: e.target.value })}
                  placeholder="86121601"
                  maxLength={8}
                  className="font-mono"
                  disabled={!!editingProdServ}
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción *</Label>
                <Input
                  value={prodServForm.descripcion}
                  onChange={(e) => setProdServForm({ ...prodServForm, descripcion: e.target.value })}
                  placeholder="Servicios de enseñanza escolar"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => { setShowProdServForm(false); setEditingProdServ(null); }}>Cancelar</Button>
                <Button onClick={() => {
                  if (!prodServForm.clave || !prodServForm.descripcion) {
                    toast({ title: 'Complete todos los campos', variant: 'destructive' });
                    return;
                  }
                  saveProdServMutation.mutate({ ...prodServForm, id: editingProdServ?.id });
                }} disabled={saveProdServMutation.isPending}>
                  {saveProdServMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingProdServ ? 'Actualizar' : 'Guardar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unidad form modal */}
      {showUnidadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">{editingUnidad ? 'Editar Unidad' : 'Nueva Unidad de Medida'}</h2>
              <button onClick={() => { setShowUnidadForm(false); setEditingUnidad(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Clave SAT *</Label>
                <Input
                  value={unidadForm.clave}
                  onChange={(e) => setUnidadForm({ ...unidadForm, clave: e.target.value.toUpperCase() })}
                  placeholder="H87"
                  maxLength={10}
                  className="font-mono"
                  disabled={!!editingUnidad}
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={unidadForm.nombre}
                  onChange={(e) => setUnidadForm({ ...unidadForm, nombre: e.target.value })}
                  placeholder="Pieza"
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                <Input
                  value={unidadForm.descripcion}
                  onChange={(e) => setUnidadForm({ ...unidadForm, descripcion: e.target.value })}
                  placeholder="Unidad de conteo"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => { setShowUnidadForm(false); setEditingUnidad(null); }}>Cancelar</Button>
                <Button onClick={() => {
                  if (!unidadForm.clave || !unidadForm.nombre) {
                    toast({ title: 'Complete todos los campos', variant: 'destructive' });
                    return;
                  }
                  saveUnidadMutation.mutate({ ...unidadForm, id: editingUnidad?.id });
                }} disabled={saveUnidadMutation.isPending}>
                  {saveUnidadMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingUnidad ? 'Actualizar' : 'Guardar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
